/**
 * Theme Context
 * Manages light/dark theme preference at the individual user level.
 *
 * Persistence: localStorage (key: 'tcgeasy-theme')
 * DOM application: [data-theme="dark"] on <html>
 *
 * Two aesthetic options:
 *  - light: White/Red/Black — clean, bright content surfaces
 *  - dark:  Black/Red/White — premium, dark content surfaces
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  theme: ThemeMode
  isDark: boolean
  setTheme: (mode: ThemeMode) => void
  toggleTheme: () => void
}

const STORAGE_KEY = 'tcgeasy-theme'
const DEFAULT_THEME: ThemeMode = 'light'

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function applyTheme(mode: ThemeMode) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (mode === 'dark') {
    root.setAttribute('data-theme', 'dark')
    root.style.colorScheme = 'dark'
  } else {
    root.setAttribute('data-theme', 'light')
    root.style.colorScheme = 'light'
  }
  // Force repaint so every CSS var re-evaluates immediately
  document.body.style.transition = 'none'
  void document.body.offsetHeight          // layout flush
  document.body.style.transition = ''
  console.log(`[Theme] Applied "${mode}" — data-theme="${root.getAttribute('data-theme')}"`)
}

function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage unavailable — use default
  }
  return DEFAULT_THEME
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const initial = getStoredTheme()
    if (typeof document !== 'undefined') {
      applyTheme(initial)
    }
    return initial
  })

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode)
    applyTheme(mode)
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // ignore
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }, [theme, setTheme])

  // Keep DOM in sync if state changes by other means
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}

export default ThemeProvider
